import { useEffect, useId, useMemo, useState } from 'react';
import { errorMessage } from '../api/client';
import type { ListDdStatesResponse, SetDdStatesResponse } from '../api/types';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';

/**
 * The DD STATE ROSTER (issue #157): which states the company operates in, is
 * registered in, or has employees in — a multi-select of the 50 states and DC.
 * Each chosen state gets its own checklist categories with that state's real
 * filings ("Tax Docs › State (Ohio) › Sales & Use Tax"); un-choosing one removes
 * them (refused while a document is filed there — the server says so).
 * Presentational: the app passes the loaded roster and its save call.
 */
export function StateRoster({
  data,
  onSave,
}: {
  data: ListDdStatesResponse;
  /** PUT the full set of chosen codes. */
  onSave: (codes: string[]) => Promise<SetDdStatesResponse>;
}) {
  const savedCodes = useMemo(() => data.states.map((s) => s.code).sort(), [data.states]);
  const [selected, setSelected] = useState<string[]>(savedCodes);
  const [filter, setFilter] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const filterId = useId();

  // A refetch after saving (or elsewhere) resets the selection to what is stored.
  useEffect(() => setSelected(savedCodes), [savedCodes]);

  if (data.placeholders.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This checklist has no per-state items to choose states for.
      </p>
    );
  }

  const nameOf = new Map(data.jurisdictions.map((j) => [j.code, j.name]));
  const chosen = new Set(selected);
  const dirty = [...chosen].sort().join(',') !== savedCodes.join(',');
  const needle = filter.trim().toLowerCase();
  const visible = data.jurisdictions.filter(
    (j) =>
      needle === '' || j.name.toLowerCase().includes(needle) || j.code.toLowerCase() === needle,
  );

  function toggle(code: string) {
    setNotice(null);
    setSelected((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));
  }

  async function save() {
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      const res = await onSave([...chosen].sort());
      const names = (codes: string[]) => codes.map((c) => nameOf.get(c) ?? c).join(', ');
      const parts = [
        res.added.length > 0 ? `Added ${names(res.added)}` : null,
        res.removed.length > 0 ? `Removed ${names(res.removed)}` : null,
      ].filter((p): p is string => p !== null);
      setNotice(parts.length > 0 ? `${parts.join('. ')}.` : 'No change.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  const chosenNames = [...chosen].map((c) => nameOf.get(c) ?? c).sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-3">
      <p className="text-sm" aria-live="polite">
        {chosenNames.length === 0 ? (
          <span className="font-medium text-danger">
            No states chosen yet — choose every state the company operates in, is registered in, or
            has employees in.
          </span>
        ) : (
          <>
            <span className="font-medium">Chosen ({chosenNames.length}):</span>{' '}
            {chosenNames.join(', ')}
          </>
        )}
      </p>
      <div className="max-w-xs space-y-1">
        <Label htmlFor={filterId}>Find a state</Label>
        <Input
          id={filterId}
          value={filter}
          placeholder="Type a state name"
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>
      <fieldset>
        <legend className="sr-only">States the company operates in</legend>
        <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((j) => (
            <li key={j.code}>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={chosen.has(j.code)}
                  disabled={pending}
                  onChange={() => toggle(j.code)}
                />
                {j.name}
              </label>
            </li>
          ))}
          {visible.length === 0 ? (
            <li className="text-sm text-muted-foreground">No state matches “{filter}”.</li>
          ) : null}
        </ul>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" disabled={!dirty || pending} onClick={() => void save()}>
          {pending ? 'Saving…' : 'Save states'}
        </Button>
        {dirty && !pending ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSelected(savedCodes);
              setError(null);
            }}
          >
            Undo changes
          </Button>
        ) : null}
        {notice ? <span className="text-sm text-ink-2">{notice}</span> : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Each state gets its own checklist items named after that state&apos;s actual filings.
        Removing a state removes its items.
      </p>
    </div>
  );
}
